import React from 'react';
import { Mic, FileText, ClipboardList, Sparkles } from 'lucide-react';
import V2Layout from '../components/V2Layout';
import {
  FadeUp, SectionHead, CtaGhost, PageHero, Check, RuleLabel, TextLink,
  OSWindow, BigStat, ClosingCta,
} from '../components/V2Shared';
import { CLINICAL_AI } from '../data/site';

const ICONS = { 'AI Scribe': Mic, 'AI Treatment Planner': ClipboardList, 'Voice Perio': FileText, 'AI Copilot': Sparkles };

/* product-specific vignettes */
const VIGNETTES = {
  'AI Scribe': {
    title: 'scribe / op 3 — live',
    lines: [
      { who: 'Dr. Lee', text: '“…occlusal caries on three-oh, recommending composite. Margins look clean on two-nine.”' },
      { who: 'Scribe', text: 'Chief complaint, findings, and plan structured as the conversation happens' },
      { who: 'System', text: 'Draft note ready: D2391 suggested · awaiting provider review', mono: true },
      { who: 'Dr. Lee', text: 'One read-through, one signature. Charting done before the next patient.' },
    ],
  },
  'AI Treatment Planner': {
    title: 'treatment planner / one click',
    lines: [
      { who: 'System', text: 'Inputs: today\'s findings + history + verified coverage', mono: true },
      { who: 'Planner', text: '“Several pockets have deepened to 5mm+ — bacteria are collecting where brushing can\'t reach. A deep cleaning removes this before it progresses…”' },
      { who: 'Planner', text: 'Phase 1: urgent perio therapy · Phase 2: restorative · sequenced with benefits' },
      { who: 'System', text: 'Visual plan generated in seconds · sent to the patient portal', mono: true },
    ],
  },
  'Voice Perio': {
    title: 'voice perio / hygiene exam',
    lines: [
      { who: 'Hygienist', text: '“Three… two… three. Bleeding on two. Four… three… three…”' },
      { who: 'System', text: 'Odontogram filling in real time · BOP marked on #2', mono: true },
      { who: 'Hygienist', text: 'Hands never leave the instruments. No second pass at the keyboard.' },
    ],
  },
  'AI Copilot': {
    title: 'copilot / chairside',
    lines: [
      { who: 'Dr. Lee', text: '“What changed since her last visit?”' },
      { who: 'Copilot', text: 'Pocket depths up on the lower left quadrant; the watch on #19 from March is still unrestored; she mentioned cold sensitivity to Amy on the phone last week.' },
      { who: 'Copilot', text: 'Her plan covers SRP this year; the crown would phase better in January.' },
    ],
  },
};

const V2ClinicalAi = () => (
  <V2Layout title="Clinical AI — Asha">
    <PageHero
      eyebrow="Clinical AI / inside the operatory"
      title={<>The chart writes itself. <span className="text-[#1a46e5]">You see patients.</span></>}
      body="Four AI products that live inside the chart — a scribe that documents while you talk, a planner that turns findings into plans patients understand, perio charting by voice, and a copilot that actually knows the patient in the chair."
    >
      <CtaGhost to="/book-demo">See a note write itself</CtaGhost>
      <TextLink to="/v2/platform">Built into the Asha PMS</TextLink>
    </PageHero>

    {/* product deep dives */}
    <section className="px-6 py-14">
      <div className="mx-auto max-w-7xl space-y-20 md:space-y-28">
        {CLINICAL_AI.map((c, i) => {
          const Icon = ICONS[c.name];
          const vig = VIGNETTES[c.name];
          const flip = i % 2 === 1;
          return (
            <div key={c.name} className="scroll-mt-32">
              <RuleLabel label={`${c.tag} / ${c.name}`} />
              <div className={`mt-10 grid items-center gap-12 lg:grid-cols-2 lg:gap-20 ${flip ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                <FadeUp>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eef3ff]">
                    <Icon className="h-5 w-5 text-[#1a46e5]" />
                  </span>
                  <h2 className="v2-display mt-6 text-[32px] font-semibold leading-[1.08] text-[#0a1633] md:text-[42px]">
                    {c.name}
                  </h2>
                  <p className="mt-4 max-w-xl text-[16px] leading-relaxed text-[#3f4d6e]">{c.detail}</p>
                  <ul className="mt-7 space-y-2.5">
                    {c.points.map((pt) => (
                      <Check key={pt}>{pt}</Check>
                    ))}
                  </ul>
                </FadeUp>
                <FadeUp delay={0.12}>
                  <div className="v2-cross relative">
                    <div className="v2-canvas rounded-2xl p-7 md:p-9">
                      <OSWindow title={vig.title}>
                        <div className="space-y-3 bg-white p-5">
                          {vig.lines.map((l, j) => (
                            <div key={j} className="flex gap-3">
                              <span className={`v2-mono w-[72px] shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                                l.mono || l.who === 'System' ? 'text-[#0f9d76]' : l.who.startsWith('Dr') || l.who === 'Hygienist' ? 'text-[#7c89a8]' : 'text-[#1a46e5]'
                              }`}>
                                {l.who}
                              </span>
                              <p className={`text-[13.5px] leading-relaxed ${
                                l.mono || l.who === 'System' ? 'v2-mono text-[12px] text-[#7c89a8]' : 'text-[#3f4d6e]'
                              }`}>
                                {l.text}
                              </p>
                            </div>
                          ))}
                        </div>
                      </OSWindow>
                    </div>
                  </div>
                </FadeUp>
              </div>
            </div>
          );
        })}
      </div>
    </section>

    {/* why it works panel */}
    <section className="px-6 py-24 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <SectionHead
            dark
            center
            eyebrow="why it works here"
            title={<>Clinical AI is only as good as the chart it can see.</>}
            body="Because the Scribe, Planner, and Copilot live inside the same operating system as the schedule, the ledger, and the insurance breakdown, they reason with the whole patient — not a transcript in a vacuum. The note becomes the claim. The finding becomes the plan. The plan respects the coverage."
          />
          <FadeUp delay={0.15}>
            <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-y-10 border-t border-white/15 pt-10 md:grid-cols-4">
              <BigStat dark value="4" label="clinical ai products" />
              <BigStat dark value="1" label="chart they share" />
              <BigStat dark value="100%" label="provider-reviewed" />
              <BigStat dark value="0" label="copy-paste between tools" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <ClosingCta
      title="Finish your notes before the patient leaves the chair."
      body="Bring a typical exam to the demo and watch the Scribe, Planner, and Copilot work it live."
    />
  </V2Layout>
);

export default V2ClinicalAi;
